/**
 * FullscreenService — Manages fullscreen mode and screen orientation.
 *
 * Responsibilities:
 * - Toggle fullscreen on/off using the Fullscreen API
 * - Lock screen orientation to landscape on mobile devices
 * - Auto-detect mobile and apply landscape preference
 * - Provide UI callbacks for button state updates
 */

export type FullscreenChangeCallback = (isFullscreen: boolean) => void;

export interface FullscreenState {
  /** Whether the app is currently in fullscreen mode. */
  isFullscreen: boolean;
  /** Whether fullscreen is supported by the browser. */
  isSupported: boolean;
  /** Whether the device is detected as mobile. */
  isMobile: boolean;
  /** Current screen orientation type. */
  orientation: string;
}

const FULLSCREEN_CHANGE_EVENT = 'fullscreenchange';
const ORIENTATION_CHANGE_EVENT = 'orientationchange';
const PORTRAIT_MEDIA_QUERY = '(orientation: portrait)';

/** The parts of the Screen Orientation API the service needs (lib.dom does
 *  not type `lock`/`unlock` — they exist in modern mobile browsers). */
interface OrientationController {
  type: string;
  lock?: (orientation: string) => Promise<void>;
  unlock?: () => void;
}

export class FullscreenService {
  private _onChangeCallbacks: FullscreenChangeCallback[] = [];
  private _onOrientationChangeCallbacks: (() => void)[] = [];
  private _isFullscreen: boolean = false;
  private _isMobile: boolean;
  private _orientationLock: OrientationController | null = null;
  /**
   * True when beginGameOrientation() entered fullscreen ITSELF (the game
   * owns the fullscreen state). releaseGameOrientation() must exit it
   * again. When the USER entered fullscreen (via the ⛶ button), the game
   * leaves the fullscreen alone on exit and only releases the lock.
   */
  private _enteredFullscreenForGame: boolean = false;

  constructor() {
    this._isMobile = this._detectMobile();
    this._bindEvents();
  }

  /** Returns the current fullscreen state. */
  getState(): FullscreenState {
    return {
      isFullscreen: this._isFullscreen,
      isSupported: this._isSupported(),
      isMobile: this._isMobile,
      orientation: this._getOrientation(),
    };
  }

  /** Whether fullscreen is currently active. */
  get isFullscreen(): boolean {
    return this._isFullscreen;
  }

  /** Whether the device is mobile. */
  get isMobile(): boolean {
    return this._isMobile;
  }

  /** Whether the screen is currently in a landscape orientation. */
  get isLandscape(): boolean {
    return this._getOrientation().startsWith('landscape');
  }

  /**
   * Subscribe to fullscreen state changes.
   * Returns an unsubscribe function.
   */
  onChange(callback: FullscreenChangeCallback): () => void {
    this._onChangeCallbacks.push(callback);
    return () => {
      const index = this._onChangeCallbacks.indexOf(callback);
      if (index !== -1) {
        this._onChangeCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to ORIENTATION changes (device rotated, lock applied or
   * released, or the orientation media query flipping). The callback is
   * fired with no arguments — it re-reads the service state. Multiple
   * sources fire (window orientationchange + Screen Orientation API
   * 'change' + matchMedia) so subscribers MUST be idempotent.
   * Returns an unsubscribe function.
   */
  onOrientationChange(callback: () => void): () => void {
    this._onOrientationChangeCallbacks.push(callback);
    return () => {
      const index = this._onOrientationChangeCallbacks.indexOf(callback);
      if (index !== -1) {
        this._onOrientationChangeCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Begin GAME-ORIENTATION enforcement (mobile devices only, no-op on
   * desktop): best-effort enter fullscreen and lock the screen to
   * landscape. Fullscreen is only requested when a lock can be applied —
   * a portrait fullscreen without a lock would hide the browser UI
   * without fixing the orientation, so it is restored on failure.
   *
   * NOTE: browsers only allow requestFullscreen (and therefore lock) from
   * a user gesture. When the match starts without a recent gesture this
   * returns false and the caller falls back to the rotate-hint overlay;
   * a TAP on that overlay re-invokes this method and, being a user
   * gesture, often succeeds.
   *
   * @returns true when the screen is now locked to landscape.
   */
  async beginGameOrientation(): Promise<boolean> {
    if (!this._isMobile) {
      return false;
    }

    const orientation = this._getOrientationController();
    if (!orientation?.lock) {
      return false; // No lock API — overlay fallback only, never fullscreen
    }

    let weEntered = false;
    if (!this._isFullscreen) {
      const entered = await this._requestFullscreen();
      if (!entered) {
        return false; // No user gesture / not allowed — overlay takes over
      }
      weEntered = true;
      // The game entered fullscreen itself — releaseGameOrientation()
      // must exit it again (the user's ⛶-button fullscreen is kept).
      this._enteredFullscreenForGame = true;
    }

    const locked = await this._lockLandscape();
    if (locked) {
      return true;
    }

    // Lock failed after we entered fullscreen — restore the pre-game
    // state (the rotate-hint overlay takes over).
    if (weEntered) {
      await this._exitFullscreen();
    }
    return false;
  }

  /**
   * Release GAME-ORIENTATION enforcement: exit fullscreen ONLY if the
   * game entered it (a user-initiated fullscreen via the ⛶ button is
   * kept), and always release the landscape lock so the landing/login
   * pages are free to be portrait.
   */
  async releaseGameOrientation(): Promise<void> {
    const gameOwnsFullscreen = this._enteredFullscreenForGame;
    this._enteredFullscreenForGame = false;
    if (!this._isMobile) {
      return;
    }

    if (gameOwnsFullscreen) {
      // exit() exits fullscreen AND unlocks (existing convention).
      await this.exit();
      return;
    }

    // The user's fullscreen (if any) stays — but the lock must go.
    if (this._orientationLock) {
      await this._unlockOrientation();
    }
  }

  /**
   * Enter fullscreen mode.
   * On mobile devices, also attempts to lock orientation to landscape.
   *
   * @returns true if fullscreen was successfully entered, false otherwise.
   */
  async enter(): Promise<boolean> {
    if (!this._isSupported()) {
      return false;
    }

    const entered = await this._requestFullscreen();
    if (!entered) {
      return false;
    }

    // On mobile, lock to landscape after entering fullscreen
    if (this._isMobile) {
      await this._lockLandscape();
    }

    return true;
  }

  /**
   * Exit fullscreen mode.
   * Also unlocks screen orientation on mobile.
   *
   * @returns true if fullscreen was successfully exited, false otherwise.
   */
  async exit(): Promise<boolean> {
    const exited = await this._exitFullscreen();
    if (!exited) {
      return false;
    }

    // Unlock orientation on mobile
    if (this._isMobile) {
      await this._unlockOrientation();
    }

    return true;
  }

  /**
   * Toggle fullscreen mode (enter if not fullscreen, exit if fullscreen).
   *
   * @returns true if the toggle was successful, false otherwise.
   */
  async toggle(): Promise<boolean> {
    if (this._isFullscreen) {
      return this.exit();
    } else {
      return this.enter();
    }
  }

  // ─── Internal Methods ─────────────────────────────────────────────────

  private _isSupported(): boolean {
    return !!(
      document.documentElement.requestFullscreen ||
      (document.documentElement as any).webkitRequestFullscreen ||
      (document.documentElement as any).msRequestFullscreen
    );
  }

  /**
   * Request fullscreen on the document element (standards API with vendor
   * fallbacks — mirroring _isSupported()). Never throws.
   */
  private async _requestFullscreen(): Promise<boolean> {
    const element = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => void | Promise<void>;
      msRequestFullscreen?: () => void | Promise<void>;
    };
    try {
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen();
      } else {
        return false;
      }
      return true;
    } catch (err) {
      console.warn('[FullscreenService] Failed to enter fullscreen:', err);
      return false;
    }
  }

  /** Exit fullscreen if active. Never throws. */
  private async _exitFullscreen(): Promise<boolean> {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else {
        return false;
      }
      return true;
    } catch (err) {
      console.warn('[FullscreenService] Failed to exit fullscreen:', err);
      return false;
    }
  }

  /** The Screen Orientation API object, or null when unavailable. */
  private _getOrientationController(): OrientationController | null {
    const orientation = screen.orientation as OrientationController | undefined;
    return orientation ?? null;
  }

  private _detectMobile(): boolean {
    // Check via user agent and touch capability
    const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
    return isMobileUA && isTouchDevice;
  }

  private _getOrientation(): string {
    if (screen.orientation && screen.orientation.type) {
      return screen.orientation.type;
    }
    // Fallback for older browsers
    return (screen as any).orientation?.orientation ?? 'unknown';
  }

  /**
   * Lock the screen to landscape (preferring the device's natural
   * 'landscape', falling back to 'landscape-primary' for browsers that
   * only know the -primary/-secondary values).
   * @returns true when the lock was applied.
   */
  private async _lockLandscape(): Promise<boolean> {
    const orientation = this._getOrientationController();
    if (!orientation?.lock) {
      return false;
    }

    try {
      await orientation.lock('landscape');
      this._orientationLock = orientation;
      return true;
    } catch (err) {
      // Some browsers only support landscape-primary/secondary
      try {
        await orientation.lock('landscape-primary');
        this._orientationLock = orientation;
        return true;
      } catch (err2) {
        console.warn('[FullscreenService] Could not lock landscape orientation:', err2);
        return false;
      }
    }
  }

  /** Release the landscape lock (if held). Never throws. */
  private async _unlockOrientation(): Promise<void> {
    const orientation = this._orientationLock;
    this._orientationLock = null;
    if (!orientation?.unlock) {
      return;
    }

    try {
      orientation.unlock();
    } catch (err) {
      console.warn('[FullscreenService] Could not unlock orientation:', err);
    }
  }

  private _bindEvents(): void {
    // Listen for fullscreen changes (user pressing Escape, browser-initiated exit)
    document.addEventListener(FULLSCREEN_CHANGE_EVENT, () => {
      const wasFullscreen = this._isFullscreen;
      this._isFullscreen = !!document.fullscreenElement;

      if (wasFullscreen !== this._isFullscreen) {
        this._notifyChange();
      }

      // If fullscreen was exited externally, unlock orientation (the
      // rotate-hint overlay is re-evaluated via the orientation events)
      if (wasFullscreen && !this._isFullscreen && this._isMobile) {
        this._enteredFullscreenForGame = false;
        this._unlockOrientation();
        this._notifyOrientationChange();
      }
    });

    // Track orientation changes (legacy mobile event — iOS + Android)
    window.addEventListener(ORIENTATION_CHANGE_EVENT, () => {
      this._notifyChange();
      this._notifyOrientationChange();
    });

    // Modern Screen Orientation API 'change' event (fires when a lock is
    // applied/released as well as on rotation)
    const orientation = this._getOrientationController();
    if (orientation && typeof (orientation as ScreenOrientation).addEventListener === 'function') {
      (orientation as ScreenOrientation).addEventListener('change', () => {
        this._notifyOrientationChange();
      });
    }

    // CSS media-query fallback (covers browsers without either event)
    if (typeof window.matchMedia === 'function') {
      const mql = window.matchMedia(PORTRAIT_MEDIA_QUERY);
      if (mql && typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', () => {
          this._notifyOrientationChange();
        });
      }
    }
  }

  private _notifyChange(): void {
    const state = this.getState();
    const callbacks = [...this._onChangeCallbacks];
    for (const callback of callbacks) {
      callback(state.isFullscreen);
    }
  }

  private _notifyOrientationChange(): void {
    const callbacks = [...this._onOrientationChangeCallbacks];
    for (const callback of callbacks) {
      callback();
    }
  }
}
